// @flow
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from 'grommet';
import { Formik } from 'formik';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { connectAPI } from 'speed-js-react';
import { FieldWrapper } from 'speed-js-react-web';
import { MapObjectFormWithModel } from 'esm-web-pro/containers/forms/FormWithModel';
import BaseSelect from 'esm-web-pro/components/forms/fields/BaseSelect';

type Props = {
    forwardRef: React.ElementRef<Formik>,
    map: { uuid: string, identity: string },
    getInfrastructureProviders: ?(mapIdentity: string) => Promise<Object>,
    infrastructureProviders: Array<Object>,
    onObjectChange: (object: { model: string }) => void,
    onProviderChange: (provider: { entity: { name: string } }) => void,
    onObjectFormChange: () => void,
    resource: ?Object,
    onLoadModelData: (modelData: Object) => void,
}

type State = {
    provider: Object,
    object: Object,
}

// eslint-disable-next-line require-jsdoc
@connectAPI((apiClient: ApiClient) => ({
    getInfrastructureProviders: {
        send: (mapIdentity) => apiClient.resources.infrastructureProvider.list({
            map_identity: mapIdentity,
        }),
        success: (response) => ({ infrastructureProviders: response.results }),
    },
}))
class NetworkDrawForm extends React.Component<Props, State> {
    static defaultProps = {
        getInfrastructureProviders: undefined,
        infrastructureProviders: [],
    };

    // eslint-disable-next-line require-jsdoc
    constructor(props: Props) {
        super(props);
        this.state = { provider: null, object: null };
    }

    // eslint-disable-next-line require-jsdoc
    componentDidMount() {
        const { getInfrastructureProviders, map } = this.props;
        if (getInfrastructureProviders) {
            getInfrastructureProviders(map.identity)
                .then((providers) => providers.results.forEach((provider) => {
                    if (provider.entity.name === process.env.REACT_APP_DEFAULT_PROVIDER) {
                        this.handleChooseProvider(provider.uuid);
                    }
                }));
        }
    }

    componentDidUpdate(prevProps: Props) {
        const { resource, onObjectFormChange } = this.props;

        if (resource && resource !== prevProps.resource) {
            onObjectFormChange();
        }
    }

    initializeForm = () => {
        const { onObjectFormChange } = this.props;
        onObjectFormChange();
    };

    handleChooseProvider = (providerUUID: string) => {
        if (!providerUUID) return;

        const { infrastructureProviders, onProviderChange } = this.props;
        const provider: Object = infrastructureProviders.find((p) => p.uuid === providerUUID);
        onProviderChange(provider);
        this.setState({ provider, object: null });
    };

    handleChooseObject = (objectId: string) => {
        if (!objectId) return;

        const { onObjectChange, map } = this.props;
        const { provider: { allowed_objects: allowedObjects } } = this.state;

        const allowedObject = allowedObjects.find((a) => a.map_identity === map.identity);
        const object = allowedObject.content_types
            .find((o) => o.id.toString() === objectId);
        this.setState({ object }, () => onObjectChange(object));
    };

    renderProviderSelect = () => {
        const { provider } = this.state;
        const { infrastructureProviders } = this.props;

        const providerChoices = infrastructureProviders.reduce((providers, p) => {
            providers[p.uuid] = p.entity.name;
            return providers;
        }, {});

        return (
            <FieldWrapper label={(
                <FormattedMessage
                    id="infrastructure-provider-select-title"
                    defaultMessage="Infrastructure provider"
                />
            )}
            >
                <BaseSelect
                    emptyLabel={(
                        <FormattedMessage
                            id="infrastructure-provider-select"
                            defaultMessage="Select infrastructure provider..."
                        />
                    )}
                    choices={providerChoices}
                    value={provider ? provider.uuid : null}
                    onChange={this.handleChooseProvider}
                />
            </FieldWrapper>
        );
    };

    renderObjectSelect = () => {
        const { provider, object } = this.state;
        const { map } = this.props;
        const objectChoices = provider.allowed_objects.find((val) => val.map_identity === map.identity).content_types
            .reduce((objects, o) => {
                objects[o.id] = o.name;
                return objects;
            }, {});

        return (
            <FieldWrapper label={(
                <FormattedMessage
                    id="infrastructure-object-select-title"
                    defaultMessage="Object"
                />
            )}
            >
                <BaseSelect
                    emptyLabel={(
                        <FormattedMessage
                            id="infrastructure-object-select"
                            defaultMessage="Select object..."
                        />
                    )}
                    choices={objectChoices}
                    value={object ? object.id : null}
                    onChange={this.handleChooseObject}
                />
            </FieldWrapper>
        );
    };

    getExcludedFields = () => {
        const { resource } = this.props;
        const { provider: { is_property_owner: isPropertyOwner, entity } } = this.state;

        const excludeFields = [...resource.excludeFields];

        if (entity.name !== process.env.REACT_APP_DEFAULT_PROVIDER) {
            excludeFields.push(...resource.defaultProviderOnlyFields);
        }

        if (!isPropertyOwner && resource.excludePropertyOwnerFields) {
            excludeFields.push(...resource.excludePropertyOwnerFields);
        }

        return excludeFields;
    };

    // eslint-disable-next-line require-jsdoc
    render() {
        const {
            infrastructureProviders, resource, map, forwardRef, onObjectFormChange, onLoadModelData,
        } = this.props;
        const { provider, object } = this.state;

        const model = object ? object.model : '';

        if (!infrastructureProviders) {
            return null;
        }

        return (
            <Box height="100%" pad={{ bottom: '60px' }}>
                <Box
                    direction="row"
                    pad={{ bottom: 'small' }}
                    align="center"
                    height="100%"
                    overflow="auto"
                    style={{ position: 'unset' }}
                >
                    <Box alignSelf="stretch" flex>
                        <Box pad={{ horizontal: 'small' }} flex={{ shrink: 0 }}>
                            {this.renderProviderSelect()}
                        </Box>
                        <Box height="12px" flex={{ shrink: 0 }} />
                        <Box pad={{ horizontal: 'small' }} flex={{ shrink: 0 }}>
                            {provider && this.renderObjectSelect()}
                        </Box>
                        {resource && (
                            <MapObjectFormWithModel
                                objectType={model}
                                forwardRef={forwardRef}
                                onLoadModelData={onLoadModelData}
                                key={resource.name}
                                resourceName={resource.name}
                                resourceArguments={[map.uuid]}
                                excludeFields={this.getExcludedFields()}
                                onChange={onObjectFormChange}
                                onInitialize={this.initializeForm}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default NetworkDrawForm;
